import React, { memo, useState } from 'react'
import { Checkbox, IconButton } from '@mui/material'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import { documentKey, documentTypeLabels, formatMoney, hasNote, sinBultos } from '../../../helpers/logisticaHelper'
import { StyledBodyCell, StyledBodyRow, EditableTextField } from '../../../ui/StyledComponents'

// Estilos e inputProps a nivel de módulo: un objeto nuevo en cada render obliga a Emotion a recalcular la fila
export const notaConNotaSx = {
    color: '#f9a825',
    backgroundColor: 'rgba(249, 168, 37, 0.15)',
    boxShadow: '0 0 8px 2px rgba(249, 168, 37, 0.6)',
    '&:hover': { backgroundColor: 'rgba(249, 168, 37, 0.25)' },
}

const notaSinNotaSx = { color: '#9e9e9e' }
const importeSx = { width: 120 }
const importeInputProps = { min: 0, step: '0.01', style: { textAlign: 'right' } }
const direccionInputProps = { maxLength: 400 }
const bultosSx = { width: 70 }
// Bultos en 0: campo en rojo también deshabilitado (el disabled de MUI pinta el texto con -webkit-text-fill-color)
const bultosCeroSx = {
    width: 70,
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#ffebee',
        '& fieldset, &.Mui-disabled fieldset': { borderColor: '#d32f2f', borderWidth: '2px' },
    },
    '& .MuiInputBase-input, & .MuiInputBase-input.Mui-disabled': {
        color: '#d32f2f',
        WebkitTextFillColor: '#d32f2f',
        fontWeight: 600,
    },
}
const bultosInputProps = { min: 0 }

// Una fila de la grilla. Está memorizada: con handlers estables solo se redibuja cuando cambia su documento,
// su tilde o el modo de solo lectura
function PlanillaRepartoRow({
    doc,
    seleccionado,
    readOnly,
    onToggleSelect,
    onEditImporte,
    onBlurImporte,
    onEditDireccion,
    onEditBultos,
    onBlurBultos,
    onOpenNota,
}) {
    // El importe se ve como moneda y pasa a número solo mientras se edita
    const [importeFocused, setImporteFocused] = useState(false)
    const key = documentKey(doc)
    const editable = !readOnly && seleccionado
    const conNota = hasNote(doc)
    const notaLabel = conNota ? 'Ver nota' : 'Agregar nota'

    return (
        <StyledBodyRow>
            <StyledBodyCell align="center">
                {documentTypeLabels[doc.documentType] ?? doc.documentType}
            </StyledBodyCell>
            <StyledBodyCell align="left">{doc.customerName}</StyledBodyCell>
            <StyledBodyCell align="right">
                <EditableTextField
                    size="small"
                    type={importeFocused ? 'number' : 'text'}
                    value={importeFocused ? doc.netAmount ?? 0 : formatMoney(doc.netAmount)}
                    onChange={(e) => onEditImporte(key, e.target.value)}
                    onFocus={() => setImporteFocused(true)}
                    onBlur={() => {
                        setImporteFocused(false)
                        onBlurImporte(doc)
                    }}
                    disabled={!editable}
                    sx={importeSx}
                    inputProps={importeInputProps}
                />
            </StyledBodyCell>
            <StyledBodyCell align="center">{doc.shippingMethod}</StyledBodyCell>
            <StyledBodyCell align="center">
                <EditableTextField
                    size="small"
                    value={doc.deliveryAddress ?? ''}
                    onChange={(e) => onEditDireccion(key, e.target.value)}
                    disabled={!editable}
                    inputProps={direccionInputProps}
                />
            </StyledBodyCell>
            <StyledBodyCell align="center">{doc.documentNumber}</StyledBodyCell>
            <StyledBodyCell align="center">{doc.orderNumber}</StyledBodyCell>
            <StyledBodyCell align="center">{doc.remitoNumber}</StyledBodyCell>
            <StyledBodyCell align="center">
                <EditableTextField
                    size="small"
                    type="number"
                    value={doc.packages ?? 0}
                    onChange={(e) => onEditBultos(key, e.target.value)}
                    onBlur={() => onBlurBultos(doc)}
                    disabled={!editable}
                    sx={sinBultos(doc) ? bultosCeroSx : bultosSx}
                    inputProps={bultosInputProps}
                />
            </StyledBodyCell>
            <StyledBodyCell align="center">
                <IconButton
                    size="small"
                    onClick={() => onOpenNota(key)}
                    disabled={!conNota && !editable}
                    title={notaLabel}
                    aria-label={notaLabel}
                    sx={conNota ? notaConNotaSx : notaSinNotaSx}
                >
                    {conNota ? <StickyNote2Icon fontSize="small" /> : <StickyNote2OutlinedIcon fontSize="small" />}
                </IconButton>
            </StyledBodyCell>
            <StyledBodyCell align="center">
                <Checkbox
                    checked={seleccionado}
                    onChange={() => onToggleSelect(key)}
                    disabled={readOnly}
                />
            </StyledBodyCell>
        </StyledBodyRow>
    )
}

export default memo(PlanillaRepartoRow)
